/** File bodies are hydrated for consumers, but never written back to Firestore. */
export const HONEYDEW_CONTENT_STORAGE = 'honeydew-files-v1';
export const HONEYDEW_MANIFEST_PATH = '_submission-manifest.json';

export interface HoneydewStoredFile {
  path: string;
  storagePath: string;
  sizeBytes: number;
  sha256: string;
  uploadedAt: string;
}

export function encodeHoneydewManifest(files: HoneydewStoredFile[]): Uint8Array {
  if (files.some((file) => file.path === HONEYDEW_MANIFEST_PATH)) {
    throw new Error(`${HONEYDEW_MANIFEST_PATH} is reserved for the submission inventory.`);
  }
  return new TextEncoder().encode(JSON.stringify({ version: 1, files }));
}

export function parseHoneydewManifest(text: string): HoneydewStoredFile[] {
  const manifest = JSON.parse(text);
  if (manifest?.version !== 1 || !Array.isArray(manifest.files)
    || manifest.files.some((file: HoneydewStoredFile) => !file
      || typeof file.path !== 'string' || !file.path
      || typeof file.storagePath !== 'string'
      || !Number.isSafeInteger(file.sizeBytes) || file.sizeBytes < 0
      || typeof file.sha256 !== 'string' || !/^[a-f0-9]{64}$/.test(file.sha256)
      || typeof file.uploadedAt !== 'string')
    || new Set(manifest.files.map((file: HoneydewStoredFile) => file.path)).size !== manifest.files.length) {
    throw new Error('Invalid Honeydew submission manifest. Re-upload the bundle.');
  }
  return manifest.files;
}

export const HONEYDEW_FILE_FIELDS: Record<string, string> = {
  taskToml: 'task/task.toml',
  taskReadmeMd: 'task/README.md',
  instructionMd: 'task/instruction.md',
  environmentDockerfile: 'task/environment/Dockerfile',
  solutionProcessMd: 'task/solution/process.md',
  solutionSolveSh: 'task/solution/solve.sh',
  testsDockerfile: 'task/tests/Dockerfile',
  testsTestSh: 'task/tests/test.sh',
  testsTestOutputsPy: 'task/tests/test_outputs.py',
  trajectorySummaryJson: 'trajectories/summary.json',
  trajectorySummaryMd: 'trajectories/summary.md',
  'skillReports.taskFixer.content': 'skill-reports/task-fixer.md',
  'skillReports.taskReview.content': 'skill-reports/task-review.md',
  'skillReports.trajectoryReview.content': 'skill-reports/trajectory-review.md',
};

export function honeydewFileFields(result: Record<string, unknown>): Record<string, string> {
  return {
    ...HONEYDEW_FILE_FIELDS,
    solutionSource: typeof result.solutionSourcePath === 'string' ? result.solutionSourcePath : '',
  };
}

/** Clone only the branches containing file bodies. */
export function withHoneydewContents(
  result: Record<string, unknown>,
  contents: Record<string, string> | null,
): Record<string, unknown> {
  const next = { ...result };
  for (const field of Object.keys(honeydewFileFields(result))) {
    const parts = field.split('.');
    let parent = next;
    for (const part of parts.slice(0, -1)) {
      parent[part] = { ...(parent[part] as Record<string, unknown> | undefined) };
      parent = parent[part] as Record<string, unknown>;
    }
    const key = parts[parts.length - 1];
    if (contents) parent[key] = contents[field] ?? '';
    else delete parent[key];
  }
  return next;
}

export function compactTaskResult(result: Record<string, unknown>): Record<string, unknown> {
  if (result?.contentStorage !== HONEYDEW_CONTENT_STORAGE) return result;
  const next = withHoneydewContents(result, null);
  if (result.submissionManifest) {
    delete next.submissionFiles;
    delete next.submissionFileList;
  }
  return next;
}
